import { sleep } from "@/utils/async";
import { getUserLicense } from "@/actions/db/get-user-license";
import { useGlobalModalContext } from "@/providers/modal-provider/modal-provider";
import { useSession } from "next-auth/react";
import { AnonServiceUsageStorage } from "@/services/storage/base/anon-service-usage-storage";
import { LicenseName } from "@/data/license.data";

type AnonServiceCheckerData = {
  serviceName: string;
  anonUserUsageCount: number;
  authTrialUserUsageCount: number;
  serviceStorage: AnonServiceUsageStorage;
  licenseValidator: (license: LicenseName) => boolean;
};

export const useAnonServiceChecker = ({
  serviceName,
  anonUserUsageCount,
  authTrialUserUsageCount,
  serviceStorage,
  licenseValidator,
}: AnonServiceCheckerData) => {
  const { showModal } = useGlobalModalContext();
  const session = useSession();

  const validate = async (): Promise<boolean> => {
    while (session.status === "loading") {
      await sleep(1000);
    }

    const usageCount = await serviceStorage.getUsageCount();
    if (
      session.status === "unauthenticated" &&
      usageCount >= anonUserUsageCount
    ) {
      showModal({
        type: "need_sign_in_modal",
        props: {
          serviceName: serviceName,
        },
      });
      return false;
    }

    if (
      session.status === "authenticated" &&
      usageCount >= authTrialUserUsageCount
    ) {
      const user = session.data.user;
      if (user?.id === undefined) {
        showModal({
          type: "need_sign_in_modal",
          props: {
            serviceName: serviceName,
          },
        });
        return false;
      }

      const userLicense = (await getUserLicense(user.id)) as LicenseName;
      if (!licenseValidator(userLicense)) {
        showModal({
          type: "license_limited_modal",
          props: {
            licenseName: userLicense,
            upgradeToMessage: `access ${serviceName}`,
            allowsMessage: "",
            shouldNavigateToPricing: true,
            availableInHigherPlanConfig: {
              isAvailableInHigherPlan:
                userLicense !== "Trial" && !licenseValidator(userLicense),
              serviceName: serviceName,
            },
          },
        });
        return false;
      }
    }

    await serviceStorage.setUsageCount(usageCount + 1);
    return true;
  };

  return { validate };
};
